import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'
import {
  useDELETE_milestoneMutation,
  useGET_milestoneByIdQuery,
  usePOST_milestoneMutation,
  usePUT_milestoneMutation,
} from 'redux/api/milestonesApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/milestoneModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IMilestone } from 'types'
import useCrudData from 'utils/customHooks/useCrudData'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import RichTextField from 'components/atoms/fields/RichTextField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const MilestoneForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/milestones'
  const editSlug = `/milestones/edit/${id}`

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('school.academic-calendar')}`,
    entityId: id,
    getEntityQuery: useGET_milestoneByIdQuery,
    createEntityMutation: usePOST_milestoneMutation,
    editEntityMutation: usePUT_milestoneMutation,
    deleteEntityMutation: useDELETE_milestoneMutation
  })

  const {
    title,
    academic_period,
    start_date,
    end_date,
    description,
    free
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IMilestone = {
          ...values,
          academic_period_id: values.academic_period.id,
        }

        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={3}>
            <DatePickerField fieldProps={{ ...start_date }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={3}>
           <DatePickerField fieldProps={{ ...end_date }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={3}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={3}>
            <SelectField
                renderChildren={false}
                fieldProps={{ ...academic_period }}
                apiCall={useGET_academicPeriodsSelectQuery}
                disabled={viewOnly}
              />
          </Grid>

          <Grid item xs={12}>
              <RichTextField
                    fieldProps={{ ...description }}
                    label={description.label}
                    disabled={viewOnly}
              />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex' }}>
              <CheckboxField fieldProps={{ ...free }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default MilestoneForm
